import { Injectable, computed, signal } from '@angular/core';
import {
  SCHEDULER_THEMES,
  SchedulerThemeId,
  SchedulerThemeOption,
  isSchedulerThemeId,
  isSchedulerToken,
} from './scheduler-theme-tokens';
import { SchedulerCustomTheme, isSafeThemeValue } from './scheduler-custom-theme';

/**
 * Re-exported so the theme system still looks like one import to its callers,
 * now that the static half lives in its own file.
 */
export { SCHEDULER_THEMES };
export type { SchedulerThemeId, SchedulerThemeOption };

/**
 * Only reached if the stylesheet has not loaded — a bare unit-test harness,
 * never the app. Matches the default theme's row height.
 */
const FALLBACK_ROW_HEIGHT = 36;

const STORAGE_KEY = 'schedulerColorTheme';
const CUSTOM_STORAGE_KEY = 'schedulerCustomThemes';
const ACTIVE_CUSTOM_STORAGE_KEY = 'schedulerActiveCustomTheme';

/**
 * Classic, deliberately: nobody's scheduler changes color until they choose a
 * direction from the cog menu. The proposals are here to be compared, not to be
 * arrived at by surprise on an upgrade.
 *
 * `_scheduler-theme.scss` mirrors this by putting classic on the bare `:root`
 * as well, so the pre-attribute paint matches. Change both together.
 */
const DEFAULT_THEME: SchedulerThemeId = 'classic';

/**
 * Owns which scheduler color direction is active.
 *
 * The whole mechanism is one attribute on `<html>`: every color in the grids
 * and the timeline resolves through `var(--sched-*)`, so switching the
 * attribute reflows the palette with no re-render, no store round trip, and
 * nothing in the components or selectors aware that themes exist.
 *
 * A user's own tweak rides on the same mechanism, one layer up: the attribute
 * still names a shipped direction, and the tokens they changed go on as inline
 * custom properties on `<html>`, which outrank the `:root[data-scheduler-theme]`
 * block. So a custom theme is only ever a base plus a sparse diff — nothing has
 * to enumerate the other ninety variables, and clearing the diff falls straight
 * back to the shipped direction.
 *
 * Applied in the constructor rather than by a route guard, because the
 * timeline, the machine dashboards and the schedule optimizer all render
 * scheduler colors and none of them share a parent component.
 */
@Injectable({ providedIn: 'root' })
export class SchedulerThemeService {
  private readonly _theme = signal<SchedulerThemeId>(DEFAULT_THEME);

  private readonly _customThemes = signal<SchedulerCustomTheme[]>([]);

  private readonly _activeCustomId = signal<string | null>(null);

  private readonly _gridRowHeight = signal(FALLBACK_ROW_HEIGHT);

  /**
   * The inline properties currently on `<html>`, so the next apply can take
   * them back off. Reading them off the element instead would mean trusting
   * that nothing else writes inline custom properties there.
   */
  private applied: string[] = [];

  /** What a user who hasn't chosen a direction gets; see `useAsDefault`. */
  private fallback: SchedulerThemeId = DEFAULT_THEME;

  /** True while the user's license hides the cog menu's Color Theme section; see `setLocked`. */
  private locked = false;

  /**
   * The user's choices, read from storage once and kept here from then on, so a choice made this
   * session still counts when storage would not take it (private browsing, a full quota).
   */
  private chosenTheme: SchedulerThemeId | null = null;
  private chosenCustomId: string | null = null;

  /** The shipped direction in force — the value on `<html data-scheduler-theme>`. */
  readonly theme = this._theme.asReadonly();

  /** The user's saved tweaks, for the cog menu. */
  readonly customThemes = this._customThemes.asReadonly();

  /** Which tweak is in force, if any. Null means the base direction, unmodified. */
  readonly activeCustomId = this._activeCustomId.asReadonly();

  readonly activeCustom = computed(() => {
    const id = this._activeCustomId();
    return id ? (this._customThemes().find((t) => t.id === id) ?? null) : null;
  });

  /**
   * Grid data-row height in px, for the two virtual scrollers.
   *
   * Read back out of the computed style rather than mapped from the theme id
   * here, so `_scheduler-theme.scss` stays the single source of truth for
   * geometry as well as color: a palette that changes its row height does not
   * also have to remember to change a number in TypeScript. A custom theme that
   * edits the row height lands in the same place, which is why every apply —
   * preview included — re-measures.
   */
  readonly gridRowHeight = this._gridRowHeight.asReadonly();

  readonly options = SCHEDULER_THEMES;

  constructor() {
    this._customThemes.set(this.readCustomThemes());
    const stored = this.read(STORAGE_KEY);
    this.chosenTheme = isSchedulerThemeId(stored) ? stored : null;
    this.chosenCustomId = this.read(ACTIVE_CUSTOM_STORAGE_KEY);
    this.restore();
  }

  /**
   * Make `theme` the direction for a user who hasn't chosen one, and show it if that's this user.
   * Nothing is saved, so a later change of default still reaches them. The app theme calls this
   * when its own default comes from the server.
   */
  useAsDefault(theme: SchedulerThemeId): void {
    this.fallback = theme;
    if (this.locked || (this.chosenTheme === null && this.chosenCustom() === null)) {
      this.apply(theme, {});
    }
  }

  /**
   * Show only the default direction, whatever the user saved. The app theme calls this once the
   * session arrives without the theme-picker claim: the cog menu's Color Theme section is hidden
   * then, so a direction or tweak saved while the license had the claim would otherwise stick
   * with no way to change it. The saved choices stay in storage and come back with the claim.
   */
  setLocked(locked: boolean): void {
    if (locked !== this.locked) {
      this.locked = locked;
      this.restore();
    }
  }

  /** Select a shipped direction, dropping any tweak that was in force. */
  set(theme: SchedulerThemeId): void {
    this.chosenTheme = theme;
    this.chosenCustomId = null;
    this.apply(theme, {});
    this._activeCustomId.set(null);
    this.write(STORAGE_KEY, theme);
    this.remove(ACTIVE_CUSTOM_STORAGE_KEY);
  }

  isActive(theme: SchedulerThemeId): boolean {
    return this._theme() === theme && this._activeCustomId() === null;
  }

  isCustomActive(id: string): boolean {
    return this._activeCustomId() === id;
  }

  selectCustom(id: string): void {
    const theme = this._customThemes().find((t) => t.id === id);
    if (!theme) {
      return;
    }
    this.chosenCustomId = id;
    this.apply(theme.base, theme.vars);
    this._activeCustomId.set(id);
    this.write(ACTIVE_CUSTOM_STORAGE_KEY, id);
  }

  /**
   * Paint a tweak without saving it — what every keystroke in the editor calls.
   *
   * Nothing is persisted, so abandoning the editor and reloading gets the user
   * back to whatever they had saved. `cancelPreview` is the same restore
   * without the reload.
   */
  preview(base: SchedulerThemeId, vars: Record<string, string>): void {
    this.apply(base, vars);
  }

  /** Put back whatever was in force before the editor opened. */
  cancelPreview(): void {
    this.restore();
  }

  /** Save a tweak (new or edited) and make it the active theme. */
  saveCustom(theme: SchedulerCustomTheme): void {
    const existing = this._customThemes();
    const known = existing.some((t) => t.id === theme.id);
    const next = known ? existing.map((t) => (t.id === theme.id ? theme : t)) : [...existing, theme];

    this._customThemes.set(next);
    this.writeCustomThemes(next);
    this.chosenCustomId = theme.id;
    this.apply(theme.base, theme.vars);
    this._activeCustomId.set(theme.id);
    this.write(ACTIVE_CUSTOM_STORAGE_KEY, theme.id);
  }

  deleteCustom(id: string): void {
    const next = this._customThemes().filter((t) => t.id !== id);
    this._customThemes.set(next);
    this.writeCustomThemes(next);

    if (this._activeCustomId() === id) {
      // Fall back to whichever shipped direction the tweak was built on, which
      // is a smaller jolt than dropping the user back to classic.
      this.set(this._theme());
    }
  }

  /** Paint what the user saved, or only the default while locked. */
  private restore(): void {
    const custom = this.locked ? null : this.chosenCustom();
    const base = this.locked ? this.fallback : (this.chosenTheme ?? this.fallback);
    this.apply(custom?.base ?? base, custom?.vars ?? {});
    this._activeCustomId.set(custom?.id ?? null);
  }

  private chosenCustom(): SchedulerCustomTheme | null {
    const id = this.chosenCustomId;
    return id ? (this._customThemes().find((t) => t.id === id) ?? null) : null;
  }

  /**
   * Saved tweaks are re-checked on the way out of storage, not just on the way
   * in: these arrive by copy/paste from other people, and a payload that was
   * imported by an older build — or edited by hand in devtools — should not be
   * able to put arbitrary custom properties on `<html>` a year later.
   */
  private readCustomThemes(): SchedulerCustomTheme[] {
    const raw = this.read(CUSTOM_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) {
      return [];
    }

    const themes: SchedulerCustomTheme[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const { id, name, base, vars } = entry as Partial<SchedulerCustomTheme>;
      if (typeof id !== 'string' || typeof name !== 'string' || !isSchedulerThemeId(base)) {
        continue;
      }
      const safeVars: Record<string, string> = {};
      for (const [token, value] of Object.entries(vars ?? {})) {
        if (typeof value === 'string' && isSchedulerToken(token) && isSafeThemeValue(value)) {
          safeVars[token] = value;
        }
      }
      themes.push({ id, name, base, vars: safeVars });
    }
    return themes;
  }

  private writeCustomThemes(themes: SchedulerCustomTheme[]): void {
    this.write(CUSTOM_STORAGE_KEY, JSON.stringify(themes));
  }

  private apply(theme: SchedulerThemeId, vars: Record<string, string>): void {
    this._theme.set(theme);

    const root = document.documentElement;
    root.setAttribute('data-scheduler-theme', theme);

    for (const name of this.applied) {
      root.style.removeProperty(name);
    }
    this.applied = [];

    for (const [name, value] of Object.entries(vars)) {
      // Re-checked here as well as at the boundaries, because this is the one
      // place the values actually reach the document.
      if (!isSchedulerToken(name) || !isSafeThemeValue(value)) {
        continue;
      }
      root.style.setProperty(name, value);
      this.applied.push(name);
    }

    this._gridRowHeight.set(this.measureRowHeight());
  }

  private measureRowHeight(): number {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--sched-grid-row-height')
      .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK_ROW_HEIGHT;
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      // Private browsing: the theme still applies for this session.
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private browsing or a full quota: the theme still applies for this
      // session, it just will not be remembered.
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // As above.
    }
  }
}
